import { useCallback, useEffect, useState } from 'react';

/**
 * テーマ（ライト / ダーク / OS 設定）。<html data-theme> に反映し localStorage に保存する。
 * 初回描画のちらつきを防ぐため、index.html のインラインスクリプトでも同じキーを読む。
 */
export type ThemePref = 'light' | 'dark' | 'system';
export const THEME_STORAGE_KEY = 'saas.theme';
const THEME_CHANGE_EVENT = 'saas:theme-change';

export function readThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(pref: ThemePref): void {
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
}

export function resolveTheme(pref: ThemePref): 'light' | 'dark' {
  if (pref !== 'system') return pref;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useTheme(): { pref: ThemePref; resolved: 'light' | 'dark'; setPref(p: ThemePref): void; toggle(): void } {
  const [pref, setPrefState] = useState<ThemePref>(() => (typeof window === 'undefined' ? 'system' : readThemePref()));
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => (typeof window === 'undefined' ? 'light' : resolveTheme(pref)));

  useEffect(() => {
    applyTheme(pref);
    setResolved(resolveTheme(pref));
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(resolveTheme(pref));
    mq.addEventListener('change', onChange);
    const synchronize = (event: Event) => {
      const value: unknown = event instanceof CustomEvent ? event.detail : readThemePref();
      setPrefState(value === 'light' || value === 'dark' ? value : 'system');
    };
    window.addEventListener(THEME_CHANGE_EVENT, synchronize);
    window.addEventListener('storage', synchronize);
    return () => {
      mq.removeEventListener('change', onChange);
      window.removeEventListener(THEME_CHANGE_EVENT, synchronize);
      window.removeEventListener('storage', synchronize);
    };
  }, [pref]);

  const setPref = useCallback((p: ThemePref) => {
    setPrefState(p);
    try {
      if (p === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
      else localStorage.setItem(THEME_STORAGE_KEY, p);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: p }));
  }, []);

  const toggle = useCallback(() => setPref(resolved === 'dark' ? 'light' : 'dark'), [resolved, setPref]);

  return { pref, resolved, setPref, toggle };
}
